import { Feature, Polygon, MultiPolygon, Point, Position } from "geojson";
import { GroupedGeoJSON } from "../../../../../../utilities/types";
import { COLORS, MAX_FEATURES, ProcessedFeatureData } from "./GeoPreviewMap.types";

export function getFeatureColor(index: number): string {
	return COLORS[index % COLORS.length];
}

function updateBounds(bounds: [number, number, number, number], lng: number, lat: number) {
	bounds[0] = Math.min(bounds[0], lng);
	bounds[1] = Math.min(bounds[1], lat);
	bounds[2] = Math.max(bounds[2], lng);
	bounds[3] = Math.max(bounds[3], lat);
}

function collectPolygonBounds(coordinates: Position[][], bounds: [number, number, number, number]) {
	for (const ring of coordinates) {
		for (const coord of ring) {
			updateBounds(bounds, coord[0], coord[1]);
		}
	}
}

export function processFeatures(inputData: GroupedGeoJSON[], groupIndex: number): ProcessedFeatureData {
	const polygonFeatures: Feature<Polygon | MultiPolygon>[] = [];
	const pointFeatures: Feature<Point>[] = [];
	const propertiesIndex = new Map<string, Record<string, unknown>>();
	const bounds: [number, number, number, number] = [Infinity, Infinity, -Infinity, -Infinity];
	let featureCount = 0;
	let hasCoords = false;

	inputData.forEach((geoData, dataIndex) => {
		const features = geoData.geoJSON.features || [];

		features.forEach((feature, featureIndex) => {
			if (featureCount >= MAX_FEATURES) return;

			const geometry = feature.geometry;
			if (!geometry) return;

			featureCount++;
			const featureKey = `${groupIndex}-${dataIndex}-${featureIndex}`;
			const color = getFeatureColor(dataIndex);
			const originalProps = feature.properties || {};
			propertiesIndex.set(featureKey, originalProps);

			if (geometry.type === "Polygon") {
				hasCoords = true;
				collectPolygonBounds(geometry.coordinates, bounds);
				polygonFeatures.push({
					type: "Feature",
					geometry: { type: "Polygon", coordinates: geometry.coordinates },
					properties: { color, featureKey },
				});
			} else if (geometry.type === "MultiPolygon") {
				hasCoords = true;
				for (const polygon of geometry.coordinates) {
					collectPolygonBounds(polygon, bounds);
				}
				polygonFeatures.push({
					type: "Feature",
					geometry: { type: "MultiPolygon", coordinates: geometry.coordinates },
					properties: { color, featureKey },
				});
			} else if (geometry.type === "Point") {
				hasCoords = true;
				const [lng, lat] = geometry.coordinates;
				updateBounds(bounds, lng, lat);
				pointFeatures.push({
					type: "Feature",
					geometry: { type: "Point", coordinates: geometry.coordinates },
					properties: { color, featureKey },
				});
			}
		});
	});

	return {
		polygonFC: { type: "FeatureCollection", features: polygonFeatures },
		pointFC: { type: "FeatureCollection", features: pointFeatures },
		totalFeatures: featureCount,
		bounds: hasCoords ? bounds : null,
		propertiesIndex,
	};
}
